/* global window */
import React, {useCallback, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import fetch from 'cross-fetch';
import flatten from 'lodash/flatten';

// Automatically initializes the iframe-resizer contentWindow handler
import 'iframe-resizer';

import Fieldset, {GridFieldset} from 'components/Fieldset';
import {GridLabel} from 'components/Label';
import GridField from 'components/GridField';
import Textbox from 'components/Textbox';
import Select from 'components/Select';
import ItemList from 'components/ItemList';
import TestimonialList from 'components/TestimonialList';
import FileUpload from 'components/FileUpload';
import StepForm from 'components/StepForm';
import toFormData from 'utils/toFormData';
import Textarea from 'components/Textarea';

const Step1 = () => {
  const {register} = useFormContext();

  return (
    <GridFieldset
      title="Product details"
      subtitle="Step 1 of 5"
      description={(
        <>
          Let’s start with some information about your product. This information will be used to
          build your sales copy.
        </>
      )}
      columns="1fr 1fr"
      layout={[
        'productName productName',
        'purpose purpose',
        'completion completion',
        'productImage productImage',
      ]}
    >
      <GridLabel title="What is the name of your product?" area="productName">
        <Textbox name="productName" placeholder="e.g., How to design well" ref={register({required: true})} maxLength={150} />
      </GridLabel>
      <GridLabel title="My awesome product is perfect for people struggling to ______." area="purpose">
        <Textbox name="purpose" placeholder="e.g. Generate leads, lose weight, etc." ref={register({required: true})} maxLength={150} />
      </GridLabel>
      <GridLabel
        title="How long does your product take to complete?"
        area="completion"
        forwardedAs="div"
        overflow
      >
        <GridField columns="1fr 1fr">
          <Textbox
            name="completion"
            type="text"
            placeholder="0"
            maxLength={5}
            ref={register({required: true})}
            onKeyDown={(evt) => {
              const charMatch = String.fromCharCode(evt.keyCode).match(/\d/) || evt.ctrlKey || evt.altKey || evt.metaKey || evt.keyCode < 40;
              if (!charMatch) {
                evt.preventDefault();
              }
            }}
          />
          <Select
            name="completionUnit"
            options={[
              {value: 'days', label: 'Days'},
              {value: 'weeks', label: 'Weeks'},
              {value: 'months', label: 'Months'},
              {value: 'years', label: 'Years'},
            ]}
            rules={{required: true}}
          />
        </GridField>
      </GridLabel>
      <GridLabel
        title="Would you like to add a product image to the top of your sales page?"
        area="productImage"
        optional
      >
        <FileUpload name="productImage" ref={register} actionLabel="Select a product image" />
      </GridLabel>
    </GridFieldset>
  );
};

Step1.fields = [
  'productName',
  'purpose',
  'completion',
  'completionUnit',
  'productImage',
];

const Step2 = ({register, control}) => (
  <Fieldset
    title="Learning plan"
    subtitle="Step 2 of 5"
    description={(
      <>
        Throughout your product, what are the things your students will learn to do?
      </>
    )}
  >
    <ItemList
      name="learningPlan"
      control={control}
      register={register}
      addLabel="Add learning item"
      placeholders={[
        'e.g. How to plan 7 days of meals…',
        'e.g. A step-by-step strategy for keyword research…',
        'e.g. A proven system to write better outreach emails…',
      ]}
      rules={{
        required: true,
        minItems: 3,
      }}
      maxLength={150}
    />
  </Fieldset>
);

Step2.fields = (formData) => (formData.learningPlan || []).map((_, idx) => `learningPlan[${idx}].value`);

const Step3 = () => (
  <Fieldset
    title="Key benefits"
    subtitle="Step 3 of 5"
    description={(
      <>
        What are the key benefits your product provides?
      </>
    )}
  >
    <ItemList
      name="keyBenefits"
      addLabel="Add benefit"
      placeholders={[
        'e.g. Grow your business…',
        'e.g. Fit into your college clothes…',
        'e.g. Generate more leads…',
      ]}
      rules={{
        required: true,
        minItems: 3,
      }}
      maxLength={150}
    />
  </Fieldset>
);

Step3.fields = (formData) => (formData.keyBenefits || []).map((_, idx) => `keyBenefits[${idx}].value`);

const Step4 = () => (
  <Fieldset
    title="Testimonials"
    subtitle="Step 4 of 5"
    description={(
      <>
        If you’d like to include some testimonials for your product in your sales copy,
        you can add them here.
      </>
    )}
  >
    <TestimonialList
      name="testimonials"
    />
  </Fieldset>
);

Step4.fields = (formData) => flatten((formData.testimonials || []).map((_, idx) => [
  `testimonials[${idx}].name`,
  `testimonials[${idx}].photo`,
  `testimonials[${idx}].quote`,
]));

const Step5 = () => {
  const {register} = useFormContext();

  return (
    <GridFieldset
      title="About yourself"
      subtitle="Step 5 of 5"
      description={(
        <>
          Last step! Tell us a bit about yourself so we can customize your copy and send it to you.
        </>
      )}
      columns="1fr"
      layout={[
        'name',
        'email',
        'bio',
        'photo',
      ]}
    >
      <GridLabel title="Your first name" area="name">
        <Textbox name="firstName" ref={register({required: true})} maxLength={100} />
      </GridLabel>
      <GridLabel title="Your email" area="email" description="This is where we’ll send the download file.">
        <Textbox name="email" ref={register({required: true, pattern: /^[^@]+@[^.]+\..+$/})} />
      </GridLabel>
      <GridLabel title="Short bio" area="bio">
        <Textarea name="bio" rows={3} ref={register} />
      </GridLabel>
      <GridLabel title="Your photo" description="Adding a photo of yourself can increase conversion!" optional area="photo">
        <FileUpload name="photo" actionLabel="Select a photo" ref={register} />
      </GridLabel>
    </GridFieldset>
  );
};

Step5.fields = [
  'firstName',
  'email',
  'bio',
  'photo',
];

const Steps = [
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
];

const SalesCopy = () => {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = useCallback(async (values) => {
    setSubmitting(true);

    try {
      await fetch('/api/sales-copy', {
        method: 'post',
        body: toFormData(values),
      });

      if (window.parentIFrame) {
        window.parentIFrame.sendMessage({complete: true});
      }
    } catch (err) {
      setSubmitting(false);
    }
  }, []);

  return (
    <StepForm
      steps={Steps}
      submitLabel="Generate sales copy"
      loadingLabel="Generating, please wait…"
      onSubmit={onSubmit}
      submitting={submitting}
      defaultValues={{
        learningPlan: [{}, {}, {}],
        keyBenefits: [{}, {}, {}],
        testimonials: [{}],
      }}
    />
  );
};

export default SalesCopy;
